import React from "react";
import { Droplets, Flame, Biohazard, Hammer, ShieldAlert, Wrench, Waves, MapPin, PhoneCall, Clock, FileCheck2, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
interface RestorationServiceItem {
  id: string;
  title: string;
  linkText?: string;
  linkTo?: string;
  icon: React.ElementType;
  description: string;
}
const servicesData: RestorationServiceItem[] = [{
  id: "water-damage",
  title: "Water Damage Restoration & Drying",
  linkText: "Water Damage Restoration",
  linkTo: "/WaterDamageFloodRestorationPage",
  icon: Droplets,
  description: "Anne Arundel County properties along the Chesapeake Bay shoreline and low-lying coastal peninsulas face high groundwater tables and sudden plumbing failures. TriServe extracts standing water, maps moisture migration behind baseboards and walls with thermal sensors, and runs commercial LGR dehumidifiers to dry framing and subfloors before secondary warping occurs."
}, {
  id: "flood-cleanup",
  title: "24/7 Emergency Flood Cleanup",
  linkText: "Emergency Flood Cleanup",
  linkTo: "/WaterDamageFloodRestorationPage",
  icon: Waves,
  description: "When tropical weather, northeast squalls, or coastal storm surges send tidal waters spilling past waterfront bulkheads and into walk-out basements, minutes matter. Our mobile crews respond around the clock with heavy submersible trash pumps, rapid water extraction rigs, and containment barriers to stop structural saturation and remove storm debris."
}, {
  id: "sewage-cleanup",
  title: "Sewage Backup Cleanup",
  linkText: "Sewage Cleanup",
  linkTo: "/SewageCleanupPage",
  icon: Biohazard,
  description: "Municipal mainline blockages, compromised drain pipes, and overwhelmed residential septic fields in Anne Arundel County introduce Category 3 blackwater loaded with dangerous pathogens. TriServe isolates contaminated zones immediately, safely extracts sewage, removes non-salvageable porous drywall and carpeting, and applies hospital-grade antimicrobial sanitizers."
}, {
  id: "fire-smoke",
  title: "Fire & Smoke Damage Restoration",
  linkText: "Fire & Smoke Restoration",
  linkTo: "/FireSmokeOdorRestorationPage",
  icon: Flame,
  description: "From chimney flare-ups in historic Annapolis residences to commercial kitchen blazes, acidic soot and toxic smoke travel fast through HVAC ducts and structural wall cavities. We provide emergency board-up and weatherproofing, hand-clean soot from salvageable finishes, and operate commercial HEPA air scrubbers to eliminate pervasive smoke odors."
}, {
  id: "mold-remediation",
  title: "Mold Remediation",
  linkText: "Mold Remediation",
  linkTo: "/MoldRemediationPage",
  icon: ShieldAlert,
  description: "Persistent maritime humidity along the Severn and Magothy rivers can cause rapid fungal colonization inside damp crawl spaces, attics, and subflooring. Our certified crews construct negative-pressure containment barriers, utilize HEPA air filtration, scrub affected structural lumber, and address source moisture to prevent cross-contamination into living quarters."
}, {
  id: "reconstruction",
  title: "Reconstruction & Structural Repair",
  linkText: "Reconstruction Services",
  linkTo: "/ReconstructionDesignBuildPage",
  icon: Hammer,
  description: "Severe flood surges or structural fire impacts often require rebuild work beyond standard drying. TriServe oversees the entire rebuild lifecycle—including load-bearing reframing, subfloor rebuilding, drywall replacement, insulation, and finish carpentry—returning Anne Arundel properties to safe, pre-loss condition with direct insurer coordination."
}, {
  id: "general-restoration",
  title: "General Emergency Property Restoration",
  linkText: "Contact Us for Emergency Service",
  linkTo: "/ContactRequestServicePage",
  icon: Wrench,
  description: "Whether handling frozen line ruptures in winter or sudden storm damage across Anne Arundel County, TriServe operates fully equipped mobile units with no walk-in retail office in Maryland. Dial 855-435-6631 anytime for direct access to on-duty local crews mobilized within ~60 minutes, with direct documentation provided for your insurance carrier."
}];
export default function AnneArundelServices() {
  return <section id="annearundelservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        
        {/* Header & Local Positioning */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            <MapPin className="size-3.5 text-primary" />
            Anne Arundel County Restoration
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Restoration and emergency recovery built for Anne Arundel County
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            With over 530 miles of tidal shoreline along the Chesapeake Bay, Severn River, and Magothy River, Anne Arundel County properties face unique risks from coastal storm surge, high water tables, and sudden structural disasters. TriServe deploys mobile emergency field crews directly to your door—operating with no walk-in retail office in Maryland and no call centers, so you speak straight to the technicians dispatching to your site.
          </p>
        </div>

        {/* Operating Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-4 p-5 rounded-lg border border-border bg-card shadow-xs">
            <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
              <Clock className="size-5" />
            </div>
            <div>
              <p className="font-semibold text-card-foreground text-sm">~60-Minute Mobile Deployment</p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 leading-normal">
                Equipped restoration trucks mobilize rapidly across Anne Arundel County to extract standing water and contain urgent structural loss.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 rounded-lg border border-border bg-card shadow-xs">
            <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
              <PhoneCall className="size-5" />
            </div>
            <div>
              <p className="font-semibold text-card-foreground text-sm">Local Crews, No Call Centers</p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 leading-normal">
                Call 855-435-6631 day or night to talk directly with on-duty technicians who assess your property damage and arrive with the gear.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 rounded-lg border border-border bg-card shadow-xs sm:col-span-2 lg:col-span-1">
            <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
              <FileCheck2 className="size-5" />
            </div>
            <div>
              <p className="font-semibold text-card-foreground text-sm">Direct Insurance Coordination</p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 leading-normal">
                We capture psychrometric moisture data, log photo records, and work directly with insurance adjusters to speed up claim approval.
              </p>
            </div>
          </div>
        </div>

        {/* Services Grid with H3 headings */}
        <div className="mb-16 md:mb-20">
          <div className="border-b border-border pb-4 mb-8">
            <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
              Field Services
            </p>
            <p className="text-2xl md:text-3xl font-bold tracking-tight text-foreground font-default">
              Full-spectrum property mitigation and recovery services
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Engineered containment and recovery protocols calibrated for Anne Arundel’s maritime climate, river basins, and historic architecture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service, index) => {
            const ServiceIcon = service.icon;
            const isGeneral = service.id === "general-restoration";
            if (isGeneral) {
              return <Card key={service.id} data-index={index} className="md:col-span-2 lg:col-span-3 flex flex-col justify-between border-border bg-card hover:border-primary/40 transition-colors shadow-xs pt-6">
                    <CardContent className="flex flex-col md:flex-row gap-6 items-center pt-0">
                      <div className="w-full md:w-1/2 space-y-4">
                        <div className="p-3 w-fit rounded-lg bg-muted text-primary border border-border">
                          <ServiceIcon className="size-6" />
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight text-card-foreground">
                          {service.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {service.description}
                        </p>
                        {service.linkTo && service.linkText && <div className="pt-2 border-t border-border/60">
                            <Link to={service.linkTo} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline group">
                              Learn more about {service.linkText}
                              <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                            </Link>
                          </div>}
                      </div>
                      <div className="w-full md:w-1/2 h-64 md:h-full min-h-[220px]">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_water_pipe_drying_equipment.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_water_pipe_drying_equipment.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_water_pipe_drying_equipment.png-wvc-sizes" alt="TriServe restoration technicians performing emergency water extraction and pipe rupture cleanup with professional drying equipment in an Anne Arundel County, MD property" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover rounded-lg border border-border" />
                      </div>
                    </CardContent>
                  </Card>;
            }
            return <Card key={service.id} data-index={index} className="flex flex-col justify-between border-border bg-card hover:border-primary/40 transition-colors shadow-xs">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="p-3 w-fit rounded-lg bg-muted text-primary border border-border">
                      <ServiceIcon className="size-6" />
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-card-foreground">
                      {service.title}
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                    {service.linkTo && service.linkText && <div className="pt-2 border-t border-border/60">
                        <Link to={service.linkTo} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline group">
                          Learn more about {service.linkText}
                          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      </div>}
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Closing Action Prompt */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 md:p-8 rounded-xl bg-secondary text-secondary-foreground">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-xl md:text-2xl font-bold font-default">
              Need immediate mitigation in Anne Arundel County?
            </h3>
            <p className="text-sm text-secondary-foreground/85 max-w-xl">
              Connect directly with TriServe technicians at 855-435-6631. Fast mobile dispatch, on-site water extraction, and full insurance adjuster coordination.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/ContactRequestServicePage">
                Request Immediate Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
              <Link to="/EmergencyRestorationGuidePage">
                Restoration Guide
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}